// src/components/sections/TestimonialSection.jsx
import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Box, Typography, IconButton, Avatar } from "@mui/material";
import { ChevronLeft, ChevronRight, Play, Quote, Star, BadgeCheck } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { getPublicTestimonials } from "../../api/testimonials";
import SectionHeading from "../../components/ui/SectionHeading";

const BLUE = "#2563EB";
const BLUE_DARK = "#1D4ED8";
const INK = "#0B1530";

const LinkedinIcon = (props) => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" {...props}>
    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3V9zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.6c0-1.34-.02-3.06-1.87-3.06-1.87 0-2.16 1.46-2.16 2.96V21h-4V9z" />
  </svg>
);

// --- Video/thumbnail helpers -------------------------------------------------

function extractYoutubeId(url) {
  if (!url) return null;
  const match = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
  return match ? match[1] : null;
}

function resolveVideoEmbed(t) {
  if (t.videoEmbedUrl) return t.videoEmbedUrl;
  if (t.videoType === "youtube" && t.videoUrl) {
    const id = extractYoutubeId(t.videoUrl);
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : null;
  }
  return null;
}

function resolveThumbnail(t) {
  if (t.videoThumbnailUrl) return t.videoThumbnailUrl;
  if (t.videoType === "youtube" && t.videoUrl) {
    const id = extractYoutubeId(t.videoUrl);
    if (id) return `https://i.ytimg.com/vi/${id}/maxresdefault.jpg`; // try real thumbnail first
  }
  if (t.profileImage) return t.profileImage;
  return null;
}

function TestimonialVideoOrPhoto({ t, playing, onPlay }) {
  const embedUrl = resolveVideoEmbed(t);
  const hasVideo = t.videoType && t.videoType !== "none" && (embedUrl || t.videoFileUrl);
  const youtubeId = t.videoType === "youtube" && t.videoUrl ? extractYoutubeId(t.videoUrl) : null;

  const [thumbSrc, setThumbSrc] = useState(() => resolveThumbnail(t));
  const [imgFailed, setImgFailed] = useState(false);
  const triedFallbackRef = useRef(false);

  useEffect(() => {
    triedFallbackRef.current = false;
    setThumbSrc(resolveThumbnail(t));
    setImgFailed(false);
  }, [t]);

  // YouTube returns HTTP 200 with a grey 120x90 placeholder when maxresdefault
  // doesn't exist for a video, so onError won't fire — check the loaded dimensions instead.
  const handleThumbLoad = (e) => {
    const img = e.target;
    if (
      youtubeId &&
      !triedFallbackRef.current &&
      img.naturalWidth === 120 &&
      img.naturalHeight === 90
    ) {
      triedFallbackRef.current = true;
      setThumbSrc(`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`);
    }
  };

  const handleThumbError = () => {
    if (youtubeId && !triedFallbackRef.current) {
      triedFallbackRef.current = true;
      setThumbSrc(`https://i.ytimg.com/vi/${youtubeId}/hqdefault.jpg`);
      return;
    }
    if (t.profileImage && thumbSrc !== t.profileImage) {
      setThumbSrc(t.profileImage);
      return;
    }
    setImgFailed(true);
  };

  if (hasVideo && playing) {
    return (
      <Box sx={{
        position: "relative", width: "100%", aspectRatio: "16/10", borderRadius: "20px",
        overflow: "hidden", bgcolor: "#000",
        boxShadow: "0 20px 50px -12px rgba(37,99,235,0.35)",
      }}>
        {embedUrl ? (
          <Box component="iframe" src={embedUrl} title={t.name} allow="autoplay; encrypted-media" allowFullScreen
            sx={{ width: "100%", height: "100%", border: 0 }} />
        ) : (
          <Box component="video" src={t.videoFileUrl} controls autoPlay
            sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
        )}
      </Box>
    );
  }

  return (
    <Box
      component={motion.div}
      whileHover={{ y: -4 }}
      transition={{ type: "spring", stiffness: 260, damping: 20 }}
      onClick={hasVideo ? onPlay : undefined}
      sx={{
        position: "relative", width: "100%", aspectRatio: "16/10", borderRadius: "20px", overflow: "hidden",
        bgcolor: "#0B1530", cursor: hasVideo ? "pointer" : "default",
        boxShadow: "0 16px 40px -14px rgba(11,21,48,0.35)",
      }}
    >
      {thumbSrc && !imgFailed ? (
        <Box
          component="img"
          src={thumbSrc}
          alt={t.name}
          onLoad={handleThumbLoad}
          onError={handleThumbError}
          sx={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.92 }}
        />
      ) : (
        <Box sx={{
          width: "100%", height: "100%",
          background: `linear-gradient(135deg, ${BLUE_DARK} 0%, #0B1530 100%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Typography sx={{ fontSize: 56, fontWeight: 800, color: "rgba(255,255,255,0.9)", letterSpacing: 1 }}>
            {initialsFrom(t.name)}
          </Typography>
        </Box>
      )}

      <Box aria-hidden sx={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(11,21,48,0.08) 35%, rgba(11,21,48,0.82) 100%)" }} />

      {t.featured && (
        <Box sx={{
          position: "absolute", top: 14, left: 14, display: "flex", alignItems: "center", gap: 0.6,
          bgcolor: "rgba(37,99,235,0.9)", color: "#fff", fontSize: 11, fontWeight: 700,
          px: 1.2, py: 0.5, borderRadius: "999px", backdropFilter: "blur(6px)",
        }}>
          <BadgeCheck size={13} /> Featured
        </Box>
      )}

      {hasVideo && (
        <Box
          sx={{
            position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
            width: 60, height: 60, borderRadius: "50%",
            bgcolor: "rgba(37,99,235,0.22)",
            border: "1.5px solid rgba(255,255,255,0.4)", backdropFilter: "blur(8px)",
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 0 0 8px rgba(37,99,235,0.14)",
          }}
        >
          <Play size={22} color="#fff" fill="#fff" style={{ marginLeft: 2 }} />
        </Box>
      )}

      <Box sx={{ position: "absolute", left: 18, bottom: 16, right: 16 }}>
        <Typography sx={{ color: "white", fontWeight: 800, fontSize: 16.5 }}>{t.name}</Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: 12.5, fontWeight: 500 }}>
          {[t.designation, t.company].filter(Boolean).join(" · ")}
        </Typography>
      </Box>
    </Box>
  );
}

function initialsFrom(name = "") {
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");
}


// --- Main section ---------------------------------------------------------------

export default function TestimonialSection() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef(null);
  const AUTO_MS = 6000;

  const { data, isLoading } = useQuery({
    queryKey: ["public-testimonials"],
    queryFn: () => getPublicTestimonials({ sort: "displayOrder", order: "asc" }).then((r) => r.data?.data ?? r.data),
  });

  const testimonials = Array.isArray(data) ? data : data?.results || [];
  const active = testimonials[index];

  const stats = useMemo(() => {
    if (!testimonials.length) return null;
    const rated = testimonials.filter((t) => t.rating != null);
    const avgRating = rated.length
      ? (rated.reduce((s, t) => s + Number(t.rating), 0) / rated.length).toFixed(1)
      : null;
    return { count: testimonials.length, avgRating };
  }, [testimonials]);

  const goTo = useCallback((dir) => {
    setPlaying(false);
    setDirection(dir);
    setIndex((i) => (i + dir + testimonials.length) % testimonials.length);
  }, [testimonials.length]);

  const goToIndex = (i) => {
    setPlaying(false);
    setDirection(i > index ? 1 : -1);
    setIndex(i);
  };

  useEffect(() => {
    if (playing || paused || testimonials.length <= 1) return;
    let start = performance.now();
    const tick = (now) => {
      const pct = Math.min(100, ((now - start) / AUTO_MS) * 100);
      setProgress(pct);
      if (pct >= 100) {
        goTo(1);
        start = now;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [index, playing, paused, testimonials.length, goTo]);

  useEffect(() => setProgress(0), [index]);

  if (isLoading) return null;
  if (!testimonials.length) return null;

  return (
    <Box
      component="section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      sx={{ position: "relative", bgcolor: "white", py: { xs: 7, md: 10 }, overflow: "hidden" }}
    >
      <Box aria-hidden sx={{
        position: "absolute", top: -120, right: -120, width: 360, height: 360, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(37,99,235,0.14) 0%, rgba(37,99,235,0) 70%)",
        pointerEvents: "none",
      }} />
      <Box aria-hidden sx={{
        position: "absolute", bottom: -140, left: -100, width: 320, height: 320, borderRadius: "50%",
        background: "radial-gradient(circle, rgba(29,78,216,0.10) 0%, rgba(29,78,216,0) 70%)",
        pointerEvents: "none",
      }} />

      <div className="section-shell" style={{ position: "relative" }}>
        <SectionHeading
          eyebrow="Success stories"
          title="Testimonials from alumni & corporate clients"
          copy="Real journeys — role changes, certifications, and career growth — told by the people who lived them."
        />

        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          sx={{
            mt: 5, display: "grid", gridTemplateColumns: { xs: "1fr", md: "0.85fr 1.15fr" },
            gap: { xs: 4, md: 5 }, alignItems: "stretch",
          }}
        >
          {/* Left — summary panel */}
          <Box sx={{
            position: "relative", borderRadius: "24px",
            background: "linear-gradient(160deg, #EFF6FF 0%, #F5F8FF 100%)",
            border: "1px solid rgba(37,99,235,0.18)",
            p: { xs: 3, md: 4 }, display: "flex", flexDirection: "column", justifyContent: "space-between",
            boxShadow: "0 20px 45px -20px rgba(37,99,235,0.35)",
          }}>
            <Box>
              <Typography sx={{
                fontWeight: 800, fontSize: { xs: 20, md: 25 }, mb: 3, lineHeight: 1.25,
                background: `linear-gradient(90deg, ${INK}, ${BLUE_DARK})`,
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
                Real Stories,<br />Incredible Journeys
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                {[
                  { value: stats.count, label: "Stories shared" },
                  ...(stats.avgRating ? [{ value: stats.avgRating, suffix: "/5.0", label: "Rated by learners" }] : []),
                  { value: testimonials.filter((t) => t.featured).length || testimonials.length, label: "Featured voices" },
                ].map((s, i) => (
                  <Box key={i}>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: 22, md: 28 }, color: INK, display: "flex", alignItems: "baseline", gap: 0.4 }}>
                      {s.value}
                      {s.suffix && <Typography component="span" sx={{ fontSize: 13, color: "#94A3B8", fontWeight: 700 }}>{s.suffix}</Typography>}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#4B5563", fontWeight: 600 }}>{s.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <AnimatePresence mode="wait">
              {active && (
                <Box
                  component={motion.div}
                  key={active.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  sx={{
                    mt: 4, p: 2.5, borderRadius: "16px", bgcolor: "white",
                    border: "1px solid rgba(37,99,235,0.15)",
                    position: "relative", overflow: "hidden",
                  }}
                >
                  <Quote size={64} color={BLUE} style={{ position: "absolute", top: -10, right: -6, opacity: 0.08 }} />
                  <Quote size={18} color={BLUE} style={{ marginBottom: 8 }} />
                  <Typography sx={{
                    fontSize: 13.5, color: "#334155", lineHeight: 1.7,
                    display: "-webkit-box", WebkitLineClamp: 5, WebkitBoxOrient: "vertical", overflow: "hidden",
                  }}>
                    {active.testimonialText}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Avatar src={active.profileImage || undefined} sx={{ width: 30, height: 30, bgcolor: BLUE, fontSize: 13, fontWeight: 700 }}>
                        {initialsFrom(active.name)}
                      </Avatar>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#0F172A" }}>{active.name}</Typography>
                    </Box>
                    {active.linkedinUrl && (
                      <Box component="a" href={active.linkedinUrl} target="_blank" rel="noopener noreferrer"
                        sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#0A66C2", textDecoration: "none", fontSize: 12, fontWeight: 700 }}>
                        <LinkedinIcon size={14} /> Profile
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </AnimatePresence>
          </Box>

          {/* Right — video/photo carousel */}
          <Box sx={{ position: "relative" }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#64748B", mb: 2 }}>
              Hear from our alumni at top companies
            </Typography>

            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={active?.id}
                custom={direction}
                initial={{ opacity: 0, x: 30 * direction }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 * direction }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                {active && <TestimonialVideoOrPhoto t={active} playing={playing} onPlay={() => setPlaying(true)} />}
              </motion.div>
            </AnimatePresence>

            {active?.rating != null && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 1.5 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={14} color={i < Math.round(active.rating) ? "#F59E0B" : "#E2E8F0"}
                    fill={i < Math.round(active.rating) ? "#F59E0B" : "none"} />
                ))}
                <Typography sx={{ fontSize: 12, color: "#64748B", fontWeight: 600, ml: 0.5 }}>{active.rating}/5.0</Typography>
              </Box>
            )}

            {testimonials.length > 1 && (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mt: 3 }}>
                <IconButton onClick={() => goTo(-1)} size="small"
                  sx={{ border: "1px solid rgba(37,99,235,0.25)", borderRadius: "8px", "&:hover": { bgcolor: "rgba(37,99,235,0.08)" } }}>
                  <ChevronLeft size={16} />
                </IconButton>

                <Box sx={{ display: "flex", gap: 0.8 }}>
                  {testimonials.map((t, i) => (
                    <Box key={t.id} onClick={() => goToIndex(i)}
                      sx={{
                        position: "relative", width: i === index ? 26 : 6, height: 6, borderRadius: 3, cursor: "pointer",
                        bgcolor: i === index ? "rgba(37,99,235,0.2)" : "rgba(15,23,42,0.15)",
                        overflow: "hidden", transition: "width 0.25s ease",
                      }}
                    >
                      {i === index && !playing && (
                        <Box sx={{
                          position: "absolute", left: 0, top: 0, bottom: 0, width: `${progress}%`,
                          bgcolor: BLUE, borderRadius: 3,
                        }} />
                      )}
                      {i === index && playing && (
                        <Box sx={{ position: "absolute", inset: 0, bgcolor: BLUE, borderRadius: 3 }} />
                      )}
                    </Box>
                  ))}
                </Box>

                <IconButton onClick={() => goTo(1)} size="small"
                  sx={{ border: "1px solid rgba(37,99,235,0.25)", borderRadius: "8px", "&:hover": { bgcolor: "rgba(37,99,235,0.08)" } }}>
                  <ChevronRight size={16} />
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      </div>
    </Box>
  );
}